import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Divider,
  Stack,
  Link,
  Grid2,
  Paper,
  Radio,
} from '@mui/material'
import theme from 'core/Theme'
import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import { HomeIcon } from 'components/images/HomeIcon'
import { KButton } from 'components/core/KButton'
import { AddressEditDialog } from 'components/account/AddressEditDialog'
import { Address, AddressType } from 'models/Address'

const AddressItem = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2, 2),
  gap: theme.spacing(4),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}))

export interface AddressListProps {
  addresses: Address[]
  addressType: AddressType
  onSave?: (data: Address) => Promise<void>
  onDelete?: (addressId?: string) => Promise<void>
  onSelectAddress?: (address: Address) => void
  selectionMode?: boolean
  selectedAddress?: Address
  hideActionButtons?: boolean
}

export const AddressList = ({
  addresses,
  addressType,
  onSave,
  onDelete,
  onSelectAddress,
  selectionMode = false,
  selectedAddress,
  hideActionButtons = false,
}: AddressListProps) => {
  const { t } = useTranslation()
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [currentSelectedAddress, setCurrentSelectedAddress] = useState<
    Address | undefined
  >(selectedAddress)

  useEffect(() => {
    setCurrentSelectedAddress(selectedAddress)
  }, [selectedAddress])

  const displayTitle =
    addressType === AddressType.Billing
      ? t('Billing addresses')
      : t('Delivery addresses')

  const displayEmptyMessage =
    addressType === AddressType.Billing
      ? t('No billing addresses found.')
      : t('No delivery addresses found.')

  const onEditClick = (address?: Address) => {
    setCurrentSelectedAddress(address)
    setOpenEditDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenEditDialog(false)
    setCurrentSelectedAddress(undefined)
  }

  const handleSelectAddress = (address: Address) => {
    setCurrentSelectedAddress(address)
    if (onSelectAddress) {
      onSelectAddress(address)
    }
  }

  const handleAddressClick = (address: Address) => {
    if (selectionMode) {
      handleSelectAddress(address)
    } else {
      onEditClick(address)
    }
  }

  const handleSaveChanges = () => {
    if (currentSelectedAddress && onSelectAddress) {
      onSelectAddress(currentSelectedAddress)
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        backgroundColor: 'background.paper',
        p: selectionMode ? 0 : 3,
        height: '100%',
        flexDirection: 'column',
        display: 'flex',
      }}
    >
      {!selectionMode && (
        <>
          <Typography variant="h4">{displayTitle}</Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}
      <Stack spacing={2} sx={{ flex: 1 }}>
        {addresses.length > 0 ? (
          <Box
            sx={{
              borderRadius: 4,
              border: 1,
              borderColor: theme.palette.grey[400],
              overflow: 'hidden',
            }}
          >
            {addresses.map((each, index) => (
              <React.Fragment key={index}>
                <AddressItem onClick={() => handleAddressClick(each)}>
                  <Grid2 container spacing={2}>
                    <Grid2 size="auto">
                      <HomeIcon />
                    </Grid2>
                    <Grid2 size={8}>
                      <Typography variant="h5">{each.name}</Typography>
                      <Typography variant="body2" color="secondary">
                        {each.address}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="secondary"
                      >{`${each.city}, ${each.state}`}</Typography>
                      <Typography variant="body2">
                        <Link
                          href={`tel:${each.phone_number}`}
                          underline="none"
                          color="primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {each.phone_number}
                        </Link>
                      </Typography>
                    </Grid2>
                    <Grid2 size="grow">
                      <Box
                        sx={{
                          position: 'relative',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                        }}
                      >
                        {!selectionMode && each.default && (
                          <Typography
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                            }}
                          >
                            <strong>{t('Default')}</strong>
                          </Typography>
                        )}
                        <Box
                          sx={{
                            mt: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 1,
                          }}
                        >
                          {selectionMode && (
                            <Radio
                              checked={currentSelectedAddress?.id === each.id}
                              onChange={() => handleSelectAddress(each)}
                              value={each.id}
                              name="address-selection"
                              color="primary"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </AddressItem>
                {index !== addresses.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="secondary">
            {displayEmptyMessage}
          </Typography>
        )}
      </Stack>

      {!hideActionButtons && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'flex-end',
            marginTop: 'auto',
            pt: 3,
          }}
        >
          <KButton
            onClick={selectionMode ? handleSaveChanges : () => onEditClick()}
            disabled={selectionMode && !currentSelectedAddress}
            sx={{
              minWidth: 320,
              height: 52,
              [theme.breakpoints.down('sm')]: {
                minWidth: 240,
                height: 40,
              },
            }}
          >
            {selectionMode
              ? t('Save changes')
              : addressType === AddressType.Billing
                ? t('Add new billing address')
                : t('Add new delivery address')}
          </KButton>
        </Stack>
      )}
      <AddressEditDialog
        open={openEditDialog}
        address={currentSelectedAddress}
        editable={!!currentSelectedAddress}
        onClose={handleCloseDialog}
        onSave={onSave}
        onDelete={onDelete}
        addressType={addressType}
      />
    </Paper>
  )
}
